import { useState } from 'react'
import { Alert, Button, Flex, Form, Input, Typography, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import { VoucherResultAlert } from './VoucherResultAlert'
import { LOGIN_PATTERN, normalizeVoucherCode } from './voucherApi'
import { useVoucherActivation } from './useVoucherActivation'

/** Bosh barmoq nishoni — kassa ekranidagi bilan bir xil (ADR-030 §4) */
const TAP_HEIGHT = 52

interface FormValues {
  code: string
  login: string
}

/** Tasdiq bosqichida qotib turadigan qiymatlar */
interface Armed {
  code: string
  login: string
}

/**
 * Kartani faollashtirish formasi.
 *
 * <h2>⚠️ IKKI BOSQICH — `ConfirmAction` dagi bilan bir xil sabab</h2>
 * Amal QAYTARIB BO'LMAYDI: karta bir marta kuyadi va noto'g'ri
 * terilgan login YANGI abonent ochib, kartani o'shanga bog'laydi
 * (`VoucherServiceImpl.findOrCreate`). Xulosa tugmaning USTIDA
 * turadi, ya'ni tasodifiy ikkinchi bosish tasdiq tugmasiga TUSHMAYDI.
 *
 * ⛔ `features/cashdesk/ConfirmAction` IMPORT QILINMADI:
 * `07-FRONTEND.md` §6 — `features/*` bir-birini import qilmaydi. U
 * komponent ustiga `Idempotency-Key` ning umrini ham boshqaradi, bu
 * endpoint esa sarlavhani umuman qabul qilmaydi (`voucherApi.ts`) —
 * ya'ni nusxa olish ishlatilmaydigan mexanizmni ham sudrab kelardi.
 *
 * <h2>⚠️ Kod maydonini panel ham NORMALLASHTIRADI</h2>
 * `normalizeVoucherCode` server bilan bir xil ishlaydi. Sabab —
 * kassir EKRANDA aynan serverga ketadigan kodni ko'rsin: aks holda
 * «kod to'g'ri yozilgan, lekin topilmadi» degan holat tushuntirib
 * bo'lmaydigan bo'lardi.
 */
export function VoucherActivateForm() {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const [form] = Form.useForm<FormValues>()
  const [armed, setArmed] = useState<Armed | null>(null)
  const activation = useVoucherActivation()

  const arm = (values: FormValues) => {
    setArmed({ code: normalizeVoucherCode(values.code), login: values.login.trim() })
  }

  const confirm = async () => {
    if (armed === null) return
    const verdict = await activation.activate(armed.code, armed.login)
    setArmed(null)
    /*
      ⚠️ MAYDONLAR FAQAT BAJARILGANDA tozalanadi.

      Karta kuygan bo'lsa navbatdagi mijoz keladi va ekranda oldingi
      kodning turishi eng yomon holat: kassir uni qayta yuborib
      «karta ishlatilgan» oladi va sababini tushunmaydi.

      ⛔ Qolgan YETTI holatda esa aksincha: kassir aynan shu qiymatlarni
      TUZATADI (login xato terilgan, kod noto'g'ri o'qilgan) yoki
      O'ZGARTIRMASDAN qayta yuboradi (tarmoq uzildi). Tozalash u yerda
      ishni boshidan yozishga majbur qilardi — mijoz qarshisida turib.
    */
    if (verdict?.kind === 'ACTIVATED') form.resetFields()
  }

  return (
    <Flex vertical gap={16}>
      <Form form={form} layout="vertical" onFinish={arm} disabled={activation.running}>
        <Form.Item
          name="code"
          label={t('vouchers.codeLabel')}
          // ⚠️ Tozalash TERILAYOTGAN paytda: server ham `trim` +
          //    katta harf + bo'shliqsiz ko'radi (`normalize`).
          //
          // ⚠️ Tur TEKSHIRILADI: `resetFields` maydonni `undefined` ga
          //    qaytaradi va antd `normalize` ni o'sha qiymat bilan ham
          //    chaqiradi — to'g'ridan-to'g'ri `raw.trim()` o'sha yerda
          //    butun formani yiqitardi.
          normalize={(value: unknown) =>
            typeof value === 'string' ? normalizeVoucherCode(value) : value
          }
          rules={[{ required: true, message: t('vouchers.codeRequired') }]}
          extra={t('vouchers.codeHint')}
        >
          <Input
            size="large"
            autoFocus
            autoComplete="off"
            spellCheck={false}
            placeholder={t('vouchers.codePlaceholder')}
            style={{ height: TAP_HEIGHT, fontFamily: 'monospace', letterSpacing: 1 }}
          />
        </Form.Item>

        <Form.Item
          name="login"
          label={t('vouchers.loginLabel')}
          rules={[
            { required: true, message: t('vouchers.loginRequired') },
            { pattern: LOGIN_PATTERN, message: t('vouchers.loginPattern') },
          ]}
          extra={t('vouchers.newAccountHint')}
        >
          <Input
            size="large"
            autoComplete="off"
            spellCheck={false}
            placeholder={t('vouchers.loginPlaceholder')}
            style={{ height: TAP_HEIGHT }}
          />
        </Form.Item>

        {armed === null ? (
          <Button
            type="primary"
            htmlType="submit"
            block
            size="large"
            style={{ height: TAP_HEIGHT, fontWeight: 600 }}
          >
            {t('vouchers.submit')}
          </Button>
        ) : null}
      </Form>

      {armed !== null ? (
        <Flex vertical gap={12}>
          {/* ⚠️ XULOSA TUGMANING USTIDA (`ConfirmAction` javadoc'i):
              kassir aynan nima yuborilishini KO'RADI — noto'g'ri
              terilgan login shu yerda ushlanadi, chunki keyin uni
              qaytarib bo'lmaydi. */}
          <div
            style={{
              background: token.colorFillTertiary,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              padding: 12,
            }}
          >
            <Typography.Text style={{ display: 'block' }}>
              {t('vouchers.summaryCode', { code: armed.code })}
            </Typography.Text>
            <Typography.Text style={{ display: 'block' }}>
              {t('vouchers.summaryLogin', { login: armed.login })}
            </Typography.Text>
            <Typography.Text type="warning" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
              {t('vouchers.irreversible')}
            </Typography.Text>
          </div>

          <Button
            type="primary"
            block
            size="large"
            loading={activation.running}
            disabled={activation.running}
            style={{ height: TAP_HEIGHT, fontWeight: 600 }}
            onClick={() => void confirm()}
          >
            {t('vouchers.confirm')}
          </Button>

          <Button block type="text" disabled={activation.running} onClick={() => setArmed(null)}>
            {t('common.cancel')}
          </Button>
        </Flex>
      ) : null}

      <VoucherResultAlert verdict={activation.verdict} onClose={activation.reset} />

      {/* ⚠️ NOSOZLIK NATIJADAN ALOHIDA BANNER. Server aniq javob bergan
          holatda kassir boshqa ish qiladi, tarmoq uzilganda esa QAYTA
          URINADI — ikkalasini bir joyga qo'yish aynan shu farqni
          yo'qotardi (`useVoucherActivation` izohi). */}
      {activation.failure !== null ? (
        <VoucherFailure text={activation.failure} onClose={activation.reset} />
      ) : null}
    </Flex>
  )
}

/**
 * Haqiqiy nosozlik — QIZIL va «qayta urinib ko'ring» deydi.
 *
 * ⚠️ Aynan shu banner `OWN_RETRY` ni ma'noli qiladi: kassir bu yerda
 * qayta urinishga CHAQIRILADI, ya'ni tarmoq uzilgan urinishdan keyingi
 * ikkinchi bosish kutilgan xatti-harakat, tasodif emas.
 */
function VoucherFailure({ text, onClose }: { text: string; onClose: () => void }) {
  const { t } = useTranslation()
  return (
    <Alert
      type="error"
      showIcon
      closable
      onClose={onClose}
      message={t('vouchers.failureTitle')}
      description={
        <>
          <div>{text}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('vouchers.failureRetry')}
          </Typography.Text>
        </>
      }
    />
  )
}
